// app/src/features/timeline/renderers/eventHelpers.jsx
// Timeline render helpers moved verbatim out of AppInner (app/src/App.jsx) by PKG-04 module #1. Each
// takes the column's render context first; the original parameters follow.
import { PieIcon } from "../../../components/Icons.jsx";
import TriggerNode, { SettledMark, UnknownMark } from "../../../components/TriggerNode.jsx";
import { C } from "../../../lib/colors.js";
import { eventKey } from "../../../lib/forecast/eventIdentity.js";
import { BASE_R, evtScale } from "../../../lib/timeline/geometry.js";
import { STREAM_HALF, TETHER_GAP, TETHER_LINE_H } from "./constants.js";
// per rendered label line — matches the label's lineHeight
export function renderLabelTether(ev, above, labelY, circleY, r, lineCount) {
  if (!ev._neutral) return null;
  const labelBottom = labelY + lineCount * TETHER_LINE_H;
  const y0 = above ? labelBottom + TETHER_GAP : circleY + 2 * r + TETHER_GAP;
  const y1 = above ? circleY - TETHER_GAP : labelY - TETHER_GAP;
  const h = y1 - y0;
  if (!(h > 3)) return null;
  return (
    <div
      style={{
        position: "absolute",
        left: -0.5,
        top: y0,
        width: 1,
        height: h,
        background: C.gray,
        opacity: 0.35,
        pointerEvents: "none",
      }}
    />
  );
}

// Only the "occurred" and "did_not_occur" statuses count; "unresolved" or absent falls through to
// normal rendering.
export function resolveEventStatus(ev, activeForecast, tracking) {
  const evResolution = tracking
    ? activeForecast?.triggerResolutions?.[ev.id]?.status || null
    : null;
  return {
    isResolvedOccurred: evResolution === "occurred",
    isResolvedNotOccurred: evResolution === "did_not_occur",
  };
}

export function computeEventGeom(ctx, ev, rowId, color, above, spineY, onSpine = false) {
  const { hov, redBlueTeam, themeRaw, triggerDiameter } = ctx;
  // Hex-alpha composition (`${rbCol}cc`) and the team colors compared by identity in
  // renderEventPie both need raw hex, not var() strings.
  const C = themeRaw;
  const hovKey = rowId + "::" + eventKey(ev);
  const sc = evtScale(ev),
    baseR = Math.max(10, Math.round(BASE_R * sc)),
    r = ev.isPast ? baseR : rowId === "main" ? Math.round(triggerDiameter(ev) / 2) : baseR - 1,
    d = r * 2,
    ih = hov === hovKey;
  const rbCol =
    redBlueTeam && !ev.isPast ? (ev.direction === "yes" ? C.blueTeam : C.redTeam) : null;
  // Multi-value ("multiway") triggers are neutral: centered ON the spine (no probability channel
  // to sit above/below), never a team (red/blue) color, and their labels stay grayL.
  const col = ev._neutral ? (ev.isPast ? C.gray : C.white) : ev.isPast ? C.gray : rbCol || color;
  const labelCol = ev._neutral
    ? C.grayL
    : ev.isPast
      ? C.grayL
      : rbCol
        ? `${rbCol}cc`
        : `${color}cc`;
  const channelOffset = STREAM_HALF * 0.55;
  // `onSpine` centres the circle on the line instead of offsetting it into one of the two
  // channels. A BRANCH FORK node needs it because it is not an event sitting beside the line —
  // it IS the corner where the vertical connector meets the branch stream, and both terminate
  // on spineY.
  const circleY =
    ev._neutral || onSpine
      ? spineY - r
      : above
        ? spineY - channelOffset - r
        : spineY + channelOffset - r;
  return { hovKey, sc, baseR, r, d, ih, rbCol, col, labelCol, channelOffset, circleY };
}

export function renderEventPie(
  ctx,
  ev,
  r,
  d,
  ih,
  col,
  isResolvedOccurred,
  isResolvedNotOccurred,
  triggerCtx,
) {
  const { themeRaw } = ctx;
  // SVG fill/stroke and the `triggerCtx.color === C.blueTeam` identity checks need raw hex; the
  // compared colors flow from computeEventGeom / LANE_COLORS, also raw.
  const C = themeRaw;
  const pieBg = ev.isPast ? C.past : C.future;
  // One small mark per state, drawn centred in the d×d hit box (see
  // components/TriggerNode.jsx and the legend under the lane): a solid ring shaded by chance
  // for a live trigger, a dashed ring for a scenario's assumption, a check or cross once
  // settled. The trigger's size no longer encodes impact; the signposts table carries it.
  if (triggerCtx) {
    const triggerP = ev.p_event != null ? ev.p_event : ev.probability != null ? ev.probability : 1;
    return (
      <TriggerNode
        size={d}
        markSize={14}
        assertedOccurs={triggerP >= 0.5}
        settled={!!triggerCtx.premiseResolved || !!triggerCtx.premiseUndetermined}
        watching={!!triggerCtx.watching}
        occurred={!!triggerCtx.premiseOccurred}
        unknown={!!triggerCtx.overdue || !!triggerCtx.premiseUndetermined}
        color={ev._neutral ? (ih ? C.white : C.grayL) : triggerCtx.color}
        bg={pieBg}
        glyphOnFill={pieBg}
      />
    );
  }
  // A past trigger whose scenarios have not settled what happened (withScenarioOutcomes).
  if (ev.isPast && ev._scenarioOutcome && !isResolvedOccurred && !isResolvedNotOccurred) {
    return (
      <UnknownMark
        size={d}
        markSize={13}
        final={ev._scenarioOutcome === "undetermined"}
        color={ih ? C.grayL : C.gray}
        bg={pieBg}
      />
    );
  }
  if (ev.isPast || isResolvedOccurred || isResolvedNotOccurred) {
    const occurred = isResolvedNotOccurred
      ? false
      : isResolvedOccurred
        ? true
        : ev.occurred !== false;
    return (
      <SettledMark
        size={d}
        markSize={13}
        occurred={occurred}
        color={ih ? C.grayL : C.gray}
        bg={pieBg}
      />
    );
  }
  // A live trigger is a pie — the slice is the chance it happens, the size is how
  // far it would move the forecast (computeEventGeom).
  const chance = ev.p_event != null ? ev.p_event : (ev.probability ?? null);
  return (
    <PieIcon p={chance ?? 0} sz={d} color={ih ? C.white : ev._neutral ? C.grayL : col} bg={pieBg} />
  );
}

// Promoted/collapsed state is scoped per row so a click on a branch event can't
// toggle the same id on the main timeline (or any other branch). Keys are
// `${rowId}::${evId}` — rowId is "main", "compare-<n>", or "branch-<id>".
export function rowKey(rowId, evId) {
  return `${rowId}::${evId}`;
}
